// NEXRAD radar mosaic raster layer family.
//
// Iowa State Mesonet serves a CORS-friendly NEXRAD reflectivity mosaic
// that auto-refreshes server-side every ~5 min. We add it beneath the
// airspace polygons so route lines and TFRs always remain readable on
// top.
//
// Extracted from addOverlays.ts as HI-1 batch 6b (audit 2026-05-22).
// Pure mechanical move — every source/layer/paint expression and
// minzoom/maxzoom is byte-identical to its pre-extraction form. The
// time-scrubber + variant-toggle effects in MapView still call
// setTiles() with nexradTileUrl(...) when the user changes them.

import type { Map as MlMap } from 'maplibre-gl';
import { nexradTileUrl } from '../mapFilters';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useUiStore } from '../../stores/uiStore';

/**
 * Mount the NEXRAD source + raster layer. Idempotent on the source.
 */
export function addNexradLayers(map: MlMap): void {
  if (!map.getSource('nexrad')) {
    map.addSource('nexrad', {
      type: 'raster',
      // HI-7 audit 2026-05-22: proxy through /api/tile (default classic
      // variant, current time). The time-scrubber + variant-toggle
      // effects in MapView call setTiles() with the appropriate proxied
      // URL when the user changes them.
      tiles: [
        nexradTileUrl(0, 'classic'),
      ],
      tileSize: 256,
      attribution: 'Radar: NOAA MRMS · Iowa State Mesonet',
      // The NEXRAD mosaic is rendered globally but only meaningful over
      // CONUS / coastal waters. Going past z=10 starts pixelating since
      // native resolution is 0.01° (~1 km).
      minzoom: 0,
      maxzoom: 10,
    });
    // Insert beneath the SUA / TFR / ownship / route layers. Find the
    // first overlay layer id and place radar before it; if none exists
    // yet, add to the top of the stack — it'll be re-ordered
    // automatically when later layers are added.
    const beforeId = (() => {
      const candidates = ['sua-fill', 'tfr-fill', 'fires-fill'];
      for (const id of candidates) if (map.getLayer(id)) return id;
      return undefined;
    })();
    map.addLayer(
      {
        id: 'nexrad-raster',
        type: 'raster',
        source: 'nexrad',
        layout: { visibility: 'none' },
        paint: {
          // 0.7 opacity lets the chart underneath show through at
          // light reflectivity; intense returns still read clearly.
          'raster-opacity': 0.72,
          // Slight saturation boost so light precip is visible against
          // busy chart backgrounds.
          'raster-saturation': 0.1,
        },
      },
      beforeId,
    );
  }
}

registerLayerFamily({
  id: 'nexrad',
  label: 'NEXRAD radar',
  sourceId: 'nexrad',
  layerIds: ['nexrad-raster'],
  mount: (map) => addNexradLayers(map),
  visibilitySelector: (l) => l.nexrad,
  // applyState: visibility + tile URL (time-scrubber) in one call.
  // Called on styledata rebuild AND from the ONE MapView useEffect
  // whenever nexradOffsetMin / nexradVariant change.
  applyState: (map) => {
    if (!map.getLayer('nexrad-raster')) return;
    const show = useLayerVisibilityStore.getState().layers.nexrad;
    map.setLayoutProperty('nexrad-raster', 'visibility', show ? 'visible' : 'none');
    if (show) {
      const { nexradOffsetMin, nexradVariant } = useUiStore.getState();
      const src = map.getSource('nexrad') as { setTiles?: (t: string[]) => void } | undefined;
      src?.setTiles?.([nexradTileUrl(nexradOffsetMin, nexradVariant)]);
    }
  },
});
